import axios from "axios";
import { useEffect, useState } from "react";
import { Download, Filter, Package, X, Search, Calendar } from "lucide-react";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import { showError, showSuccess } from "../../../utils/toastUtils";
import { useAdminAuth } from "../../../context/AdminAuthContext";

const ITEMS_PER_PAGE = 10;

const ProductLedger = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [ledger, setLedger] = useState([]);
  const [filteredLedger, setFilteredLedger] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { admin, isAdmin, isManager, adminRole } = useAdminAuth();

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");

  // filters
  const [actionType, setActionType] = useState("all");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Separate state for applied filters
  const [appliedFilters, setAppliedFilters] = useState({
    actionType: "all",
    selectedBranch: "all",
    fromDate: "",
    toDate: "",
  });

  useEffect(() => {
    fetchLedger();
  }, [appliedFilters]);

  useEffect(() => {
    fetchBranches();
  }, [isAdmin]);

  useEffect(() => {
    applySearchFilter();
  }, [search, ledger]);

  const fetchBranches = async () => {
    if (!isAdmin) return;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/branches/get-all`
      );
      setBranches(res.data.data || []);
    } catch {
      showError("Failed to load branches");
    }
  };

  const applySearchFilter = () => {
    if (!search.trim()) {
      setFilteredLedger(ledger);
      setCurrentPage(1);
      return;
    }

    const q = search.toLowerCase();
    const filtered = ledger.filter((l) =>
      l.product?.productName?.toLowerCase().includes(q) ||
      l.product?.brand?.toLowerCase().includes(q) ||
      l.actionType?.toLowerCase().includes(q) ||
      l.remark?.toLowerCase().includes(q) ||
      l.branch?.branchName?.toLowerCase().includes(q)
    );

    setFilteredLedger(filtered);
    setCurrentPage(1);
  };

  const fetchLedger = async () => {
    try {
      setIsLoading(true);
      const params = {};

      // Action type filter
      if (appliedFilters.actionType !== "all") {
        params.actionType = appliedFilters.actionType;
      }

      // Date range filter
      if (appliedFilters.fromDate) {
        params.fromDate = appliedFilters.fromDate;
      }
      if (appliedFilters.toDate) {
        params.toDate = appliedFilters.toDate;
      }

      // SUPER ADMIN
      if (isAdmin && appliedFilters.selectedBranch !== "all") {
        params.branch = appliedFilters.selectedBranch; // "null" OR branchId
      }

      // BRANCH MANAGER
      if (isManager) {
        params.branch = admin.assignedBranch?._id;
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/product-ledger`,
        { params }
      );

      const data = res.data.data || [];
      setLedger(data);
      setFilteredLedger(data);
    } catch (error) {
      console.error("Ledger fetch error:", error);
      showError("Failed to load ledger");
      setLedger([]);
      setFilteredLedger([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredLedger.length / ITEMS_PER_PAGE);
  const paginatedLedger = filteredLedger.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Calculate totals from filtered ledger (all data, not just current page)
  const totalStockInQty = filteredLedger.reduce(
    (sum, l) => sum + (l.quantityChange > 0 ? l.quantityChange : 0),
    0
  );

  const totalStockOutQty = filteredLedger.reduce(
    (sum, l) => sum + (l.quantityChange < 0 ? Math.abs(l.quantityChange) : 0),
    0
  );

  // Calculate total amounts (value) from filtered ledger
  const totalStockInAmount = filteredLedger.reduce((sum, l) => {
    if (l.quantityChange > 0 && l.product) {
      const mrp = l.product.mrp || 0;
      const discount = l.product.discountPercent || 0;
      const finalPrice = mrp - (mrp * discount / 100);
      return sum + (finalPrice * l.quantityChange);
    }
    return sum;
  }, 0);

  const totalStockOutAmount = filteredLedger.reduce((sum, l) => {
    if (l.quantityChange < 0 && l.product) {
      const mrp = l.product.mrp || 0;
      const discount = l.product.discountPercent || 0;
      const finalPrice = mrp - (mrp * discount / 100);
      return sum + (finalPrice * Math.abs(l.quantityChange));
    }
    return sum;
  }, 0);

  // Get active filters count
  const activeFiltersCount =
    (actionType !== "all" ? 1 : 0) +
    (selectedBranch !== "all" && isAdmin ? 1 : 0) +
    (fromDate ? 1 : 0) +
    (toDate ? 1 : 0) +
    (search.trim() ? 1 : 0);

  const handleApplyFilters = () => {
    setAppliedFilters({
      actionType,
      selectedBranch,
      fromDate,
      toDate,
    });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setActionType("all");
    setSelectedBranch("all");
    setSearch("");
    setFromDate("");
    setToDate("");
    setAppliedFilters({
      actionType: "all",
      selectedBranch: "all",
      fromDate: "",
      toDate: "",
    });
    setCurrentPage(1);
  };

  // Check if filters have changed from applied filters
  const hasUnappliedChanges =
    actionType !== appliedFilters.actionType ||
    selectedBranch !== appliedFilters.selectedBranch ||
    fromDate !== appliedFilters.fromDate ||
    toDate !== appliedFilters.toDate;

  // EXPORT PDF
  const exportPDF = async () => {
    if (!filteredLedger.length) {
      showError("No data to export");
      return;
    }

    try {
      const { jsPDF } = await import("jspdf");
      const autoTable = (await import("jspdf-autotable")).default;

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const marginX = 8;
      const marginTop = 12;
      doc.setFontSize(16);
      doc.text("Product Ledger Report", 14, 15);

      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleDateString("en-IN")}`, 14, 22);

      // Add filter info
      let yPos = 28;
      if (appliedFilters.actionType !== "all") {
        doc.text(`Action Type: ${appliedFilters.actionType}`, 14, yPos);
        yPos += 5;
      }
      if (appliedFilters.fromDate || appliedFilters.toDate) {
        doc.text(
          `Date Range: ${appliedFilters.fromDate || "Start"} to ${appliedFilters.toDate || "End"}`,
          14,
          yPos
        );
        yPos += 5;
      }
      if (appliedFilters.selectedBranch !== "all" && isAdmin) {
        const branchName = appliedFilters.selectedBranch === "null"
          ? "Global Purchase"
          : branches.find((b) => b._id === appliedFilters.selectedBranch)?.branchName;
        doc.text(`Branch: ${branchName}`, 14, yPos);
        yPos += 5;
      }

      autoTable(doc, {
        startY: marginTop + 6,

        margin: {
          left: marginX,
          right: marginX,
        },

        tableWidth: "auto",
        startY: yPos + 2,
        head: [
          [
            "Date",
            "Product",
            "Branch",
            "Action",
            "IN",
            "OUT",
            "Performed By",
            "Remark",
          ],
        ],
        body: filteredLedger.map((l) => [
          new Date(l.createdAt).toLocaleDateString("en-IN"),
          l.product?.productName || "-",
          l.branch?.branchName || "GLOBAL",
          l.actionType,
          l.quantityChange > 0 ? l.quantityChange : "-",
          l.quantityChange < 0 ? Math.abs(l.quantityChange) : "-",
          l.performedBy?.name || "-",
          l.remark || "-",
        ]),
        styles: {
          fontSize: 8,
          cellPadding: 2,
          valign: "middle",
          capitalize: true
        },

        headStyles: {
          fillColor: [237, 181, 164],
          textColor: 0,
          fontSize: 9,
          cellPadding: 3,
        },

        columnStyles: {
          0: { cellWidth: 35 }, // Product
          3: { cellWidth: 30 }, // Vendor
          5: { cellWidth: 18 }, // MRP
          6: { cellWidth: 14 }, // Qty
          8: { cellWidth: 22 }, // Final Price
        },
      });

      // Add totals at the bottom
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text(
        `Total Stock IN: ${totalStockInQty} items (INR ${totalStockInAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })})`,
        14,
        finalY
      );
      doc.text(
        `Total Stock OUT: ${totalStockOutQty} items (INR ${totalStockOutAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })})`,
        14,
        finalY + 6
      );

      doc.save(`Product_Ledger_${new Date().toISOString().split("T")[0]}.pdf`);
      showSuccess("Ledger exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      showError("Failed to export PDF");
    }
  };

  // console.log(filteredLedger)

  return (
    <div className="flex flex-col min-w-0 w-full bg-gradient-to-br px-6 min-h-screen from-[#FFF7F3] to-gray-50">
      <div className="py-2 flex items-center justify-between">
        <Breadcrumb title1="Inventory" title2="Product Ledger" />

        {/* Export Button */}
        <button
          onClick={exportPDF}
          disabled={filteredLedger.length === 0}
          className="px-4 py-2.5 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 whitespace-nowrap"
        >
          <Download size={18} />
          Export PDF
        </button>
      </div>

      {/* DASHBOARD STATS */}
      {/* <div className="mx-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                <Package className="w-6 h-6 text-white" />
              </div>
            </div>
            <p className="text-sm text-blue-700 font-medium mb-1">Total Entries</p>
            <p className="text-3xl font-bold text-blue-900">{ledger.length}</p>
            <p className="text-xs text-blue-600 mt-1">Ledger records</p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                <Download className="w-6 h-6 text-white rotate-180" />
              </div>
            </div>
            <p className="text-sm text-green-700 font-medium mb-1">Stock IN (Value)</p>
            <p className="text-3xl font-bold text-green-900">
              ₹{totalStockInAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-green-600 mt-1">{totalStockInQty} items received</p>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
                <Download className="w-6 h-6 text-white" />
              </div>
            </div>
            <p className="text-sm text-red-700 font-medium mb-1">Stock OUT (Value)</p>
            <p className="text-3xl font-bold text-red-900">
              ₹{totalStockOutAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-red-600 mt-1">{totalStockOutQty} items dispatched</p>
          </div>
        </div>
      </div> */}

      <div className="mb-6 bg-white rounded-md shadow-lg p-3">
        {/* FILTER BAR */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start gap-4">
            <div className="flex justify-between flex-1">
              {/* Search */}
              <div className="relative h-fit flex-1 min-w-[200px] max-w-[20%]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search product, action, remark..."
                  className="w-full shadow-md pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor transition"
                />
              </div>

              {/* Second Row - Date Filters */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* Action Type Filter */}
                <div className="relative">
                  <button
                    className={`px-4 py-2.5 border shadow-lg rounded-lg font-medium flex items-center gap-2 transition ${
                      actionType !== "all"
                        ? "bg-blue-50 border-blue-300 text-blue-700"
                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <Filter size={18} />
                    <select
                      value={actionType}
                      onChange={(e) => setActionType(e.target.value)}
                      className="bg-transparent border-none outline-none cursor-pointer"
                    >
                      <option value="all">All Actions</option>
                      <option value="purchase">Purchase</option>
                      <option value="assignment">Assignment</option>
                      <option value="return">Return</option>
                      <option value="adjustment">Adjustment</option>
                    </select>
                  </button>
                </div>

                {/* BRANCH FILTER — SUPER ADMIN ONLY */}
                {isAdmin && (
                  <div className="relative">
                    <select
                      value={selectedBranch}
                      onChange={(e) => setSelectedBranch(e.target.value)}
                      className="px-4 py-2.5 border shadow-lg rounded-lg font-medium w-40 bg-white border-gray-300 text-gray-700 hover:bg-gray-50 transition focus:outline-none focus:ring-1 focus:ring-accentTextColor"
                    >
                      <option value="all">All Branches</option>
                      <option value="null">Global Purchase</option>
                      {branches.map((b) => (
                        <option key={b._id} value={b._id}>
                          {b.branchName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {/* From Date */}
                {/* <div className="relative"> */}
                {/* <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4" /> */}
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="px-2 py-2 border shadow-lg rounded-lg font-medium bg-white border-gray-300 text-gray-700 hover:bg-gray-50 transition focus:outline-none focus:ring-1 focus:ring-accentTextColor"
                  placeholder="From Date"
                />
                {/* </div> */}

                {/* To Date */}
                {/* <div className="relative"> */}
                {/* <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4" /> */}
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="px-2 py-2 border shadow-lg rounded-lg font-medium bg-white border-gray-300 text-gray-700 hover:bg-gray-50 transition focus:outline-none focus:ring-1 focus:ring-accentTextColor"
                  placeholder="To Date"
                />
                {/* </div> */}

                {/* Apply Filters Button */}
                {hasUnappliedChanges && (
                  <button
                    onClick={handleApplyFilters}
                    className="px-4 py-2.5 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition flex items-center gap-2 active:scale-95"
                  >
                    <Filter size={18} />
                    Apply Filters
                  </button>
                )}

                {/* Clear Filters */}
                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="px-3 py-2.5 text-gray-600 hover:text-gray-900 font-medium flex items-center gap-2 transition border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                  >
                    <X size={18} />
                    Clear All
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Results Summary */}
          <div className="flex items-center justify-between text-sm mb-2 text-gray-600">
            <div className="flex items-center gap-2 flex-wrap">
              {appliedFilters.actionType !== "all" && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium capitalize">
                  {appliedFilters.actionType}
                </span>
              )}
              {appliedFilters.selectedBranch !== "all" && isAdmin && (
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                  {appliedFilters.selectedBranch === "null"
                    ? "Global Purchase"
                    : branches.find(
                        (b) => b._id === appliedFilters.selectedBranch,
                      )?.branchName}
                </span>
              )}
              {(appliedFilters.fromDate || appliedFilters.toDate) && (
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                  {appliedFilters.fromDate || "Start"} to{" "}
                  {appliedFilters.toDate || "End"}
                </span>
              )}
            </div>

            {activeFiltersCount > 0 && (
              <div className="text-blue-600 font-medium">
                {activeFiltersCount} filter{activeFiltersCount > 1 ? "s" : ""}{" "}
                active
              </div>
            )}
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full min-w-max border border-gray-300">
            <thead>
              <tr className="text-left bg-[#EDB5A4] text-primaryTextColor text-xs uppercase tracking-wider">
                <th className="p-3">Date & Time</th>
                <th className="p-3">Product</th>
                <th className="p-3">Branch</th>
                <th className="p-3">Action Type</th>
                <th className="p-3 text-center">Stock IN</th>
                <th className="p-3 text-center">Stock OUT</th>
                <th className="p-3">Performed By</th>
                <th className="p-3">Remark</th>
              </tr>
            </thead>

            {isLoading ? (
              <LedgerTableSkeleton rows={ITEMS_PER_PAGE} />
            ) : filteredLedger.length === 0 ? (
              <EmptyState
                hasFilters={activeFiltersCount > 0}
                onClearFilters={clearFilters}
              />
            ) : (
              <tbody className="divide-y divide-gray-200 text-sm">
                {paginatedLedger.map((l) => (
                  <tr key={l._id} className="hover:bg-gray-50 transition">
                    <td className="p-3">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">
                          {new Date(l.createdAt).toLocaleDateString("en-IN")}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(l.createdAt).toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 capitalize">
                          {l.product?.productName}
                        </span>
                        {l.product?.brand && (
                          <span className="text-xs text-gray-500 capitalize">
                            {l.product.brand}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          l.branch
                            ? "bg-blue-100 text-blue-700"
                            : "bg-purple-100 text-purple-700"
                        }`}
                      >
                        {l.branch?.branchName || "GLOBAL"}
                      </span>
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                          l.actionType === "purchase"
                            ? "bg-green-100 text-green-700"
                            : l.actionType === "assignment"
                              ? "bg-yellow-100 text-yellow-700"
                              : l.actionType === "return"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {l.actionType}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      {l.quantityChange > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[48px] px-2 py-1 bg-green-100 text-green-700 font-semibold rounded-full text-xs">
                          +{l.quantityChange}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      {l.quantityChange < 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[48px] px-2 py-1 bg-red-100 text-red-700 font-semibold rounded-full text-xs">
                          {Math.abs(l.quantityChange)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-xs font-bold text-blue-600">
                            {l.performedBy?.name?.charAt(0)?.toUpperCase() ||
                              "?"}
                          </span>
                        </div>
                        <span className="text-gray-700">
                          {l.performedBy?.name || "-"}
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-gray-600 max-w-xs truncate">
                      {l.remark || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <div>
              <span className="font-medium text-gray-900 text-sm">
                Showing <span>{filteredLedger.length}</span> of{" "}
                <span className="font-medium text-gray-900">
                  {ledger.length}
                </span>{" "}
                entries
              </span>
              <p className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </p>
            </div>

            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
              >
                Prev
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// SKELETON LOADER
const LedgerTableSkeleton = ({ rows = 10 }) => (
  <tbody>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i} className="animate-pulse">
        {Array.from({ length: 8 }).map((_, j) => (
          <td key={j} className="p-3">
            <div className="h-8 bg-gray-200 rounded" />
          </td>
        ))}
      </tr>
    ))}
  </tbody>
);

// EMPTY STATE
const EmptyState = ({ hasFilters, onClearFilters }) => (
  <tbody>
    <tr>
      <td colSpan={8} className="py-16 text-center">
        <div className="flex flex-col items-center gap-4 text-gray-500">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
            <Package className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700">
            {hasFilters ? "No ledger entries match your filters" : "No ledger entries found"}
          </h3>
          {hasFilters && (
            <button
              onClick={onClearFilters}
              className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
            >
              Clear Filters
            </button>
          )}
        </div>
      </td>
    </tr>
  </tbody>
);

export default ProductLedger;